import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { supabase, useAuth } from 'hooks/useAuth';
import { useNavigateWithParams } from 'hooks/useNavigateWithParams';
import { useToast } from 'hooks/useToast';

import { AppScrollProps } from 'components/layout/PursuitContainer';
import { RequiredAsterisk } from 'components/ui/RequiredAsterisk';
import { TextInput } from 'components/ui/TextInput';
import { Button } from 'components/ui/Button';

import { GrandLogoVert } from 'components/svg/GrandLogoVert';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import { Checkbox, CheckboxWrapper, Bumper } from 'styles/shared';
import { AuthForm, AuthWrapper } from 'styles/auth';
import { Loader } from 'components/ui/Loader';

export const Confirm = ({ appScrollToTop }: AppScrollProps) => {
    const { search } = useLocation();
    const { verifyAccount, userProfile } = useAuth();
    const navigateWithParams = useNavigateWithParams();
    const { showSuccessToast, showErrorToast } = useToast();

    const [verificationCode, setVerificationCode] = useState<string>('');
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [confirmedPassword, setConfirmedPassword] = useState<string>('');
    const [isError, setIsError] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        appScrollToTop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { email, type } = useMemo(() => {
        const urlParams = new URLSearchParams(search);
        return {
            email: urlParams.get('email') ?? '',
            type: urlParams.get('type') as 'invite' | 'recovery' | null,
        };
    }, [search]);

    const onVerificationCodeInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setVerificationCode(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onToggleAcceptTerms = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setHasAcceptedTerms(target.checked);
        },
        []
    );

    const onVerifyAccount = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSubmitting) {
                return;
            }
            setIsSubmitting(true);

            const error = await verifyAccount?.({
                verificationCode,
                email,
                type,
            });

            if (error == null) {
                setIsVerified(true);
            } else {
                setIsError(true);
                showErrorToast(error.message);
            }

            setIsSubmitting(false);
        },
        [
            email,
            isSubmitting,
            showErrorToast,
            type,
            verificationCode,
            verifyAccount,
        ]
    );

    const onPasswordInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setPassword(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onConfirmPasswordInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setConfirmedPassword(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onSetPassword = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSubmitting) {
                return;
            }
            setIsSubmitting(true);

            if (password.length < 8) {
                setIsError(true);
                showErrorToast('Your password must be at least 8 characters');
                setIsSubmitting(false);
                return;
            }

            if (password !== confirmedPassword) {
                setIsError(true);
                showErrorToast('Your passwords do not match');
                setIsSubmitting(false);
                return;
            }

            const { error } = await supabase.auth.updateUser({
                password,
            });

            if (error != null) {
                setIsError(true);
                showErrorToast(error.message);
            } else {
                showSuccessToast('Your password has been confirmed');
                navigateWithParams('/');
            }

            setIsSubmitting(false);
        },
        [
            confirmedPassword,
            isSubmitting,
            navigateWithParams,
            password,
            showErrorToast,
            showSuccessToast,
        ]
    );

    return (
        <AuthWrapper>
            <Bumper $shouldHideOnDesktop />
            <GrandLogoVert width={140} />
            <Bumper />
            {!isVerified ? (
                <AuthForm
                    onSubmit={onVerifyAccount}
                    $backgroundColor={transparencies.white60}
                >
                    <Fonts.Heading6
                        $color={colors.mountainAsh}
                    >{`Verify`}</Fonts.Heading6>
                    <Bumper />
                    <Fonts.Medium $textAlign="center">
                        {`Please verify your account by entering the 6 digit code you received in your email`}
                    </Fonts.Medium>
                    <Bumper />
                    <TextInput
                        type="password"
                        id="verification-code"
                        name="verification-code"
                        onInput={onVerificationCodeInput}
                        label="6-digit code"
                        $isError={isError}
                        required
                        $backgroundColor={transparencies.darkJungle5}
                    />
                    <Bumper />
                    <CheckboxWrapper>
                        <Checkbox
                            type="checkbox"
                            name="share"
                            onChange={onToggleAcceptTerms}
                            checked={hasAcceptedTerms}
                            required
                        />
                        <Fonts.Small>
                            {`By checking this box, I acknowledge that I accept The Grand's`}
                            &nbsp;
                            <Fonts.Small
                                as="a"
                                href="https://www.thegrand.world/termsofservice"
                                target="_blank"
                                rel="noreferrer"
                                $isUnderlined
                            >
                                {`Terms of Service`}
                            </Fonts.Small>
                            &nbsp;{`and`}&nbsp;
                            <Fonts.Small
                                as="a"
                                href="https://www.thegrand.world/privacypolicy"
                                target="_blank"
                                rel="noreferrer"
                                $isUnderlined
                            >
                                {`Privacy Policy`}
                            </Fonts.Small>
                            <RequiredAsterisk />
                        </Fonts.Small>
                    </CheckboxWrapper>
                    <Bumper />
                    <Button
                        type="submit"
                        value="Submit"
                        $isLoading={isSubmitting}
                        $isStretch
                    >
                        {`Verify`}
                    </Button>
                    <Bumper />
                    <Link to="/password-reset">
                        <Fonts.Small
                            $isUnderlined
                        >{`Invalid code? Get a new code here`}</Fonts.Small>
                    </Link>
                </AuthForm>
            ) : null}

            {isVerified ? (
                userProfile == null ? (
                    <Loader $color={colors.grandGreen} />
                ) : (
                    <AuthForm
                        onSubmit={onSetPassword}
                        $backgroundColor={transparencies.white60}
                    >
                        <Fonts.Heading6
                            $color={colors.mountainAsh}
                            $textAlign="center"
                        >{`Welcome, ${
                            userProfile?.full_name?.split(' ')[0] ??
                            'Grand Explorer'
                        }!`}</Fonts.Heading6>
                        <Bumper />
                        <TextInput
                            type="password"
                            id="password"
                            name="password"
                            onInput={onPasswordInput}
                            label="Password"
                            $isError={isError}
                            required
                            $backgroundColor={transparencies.darkJungle5}
                        />
                        <Bumper />
                        <TextInput
                            type="password"
                            id="confirm-password"
                            name="confirm-password"
                            onInput={onConfirmPasswordInput}
                            label="Confirm password"
                            $isError={isError}
                            required
                            $backgroundColor={transparencies.darkJungle5}
                        />
                        <Bumper $minHeight={spacing.xLarge} />
                        <Button
                            type="submit"
                            value="Submit"
                            $isLoading={isSubmitting}
                            $isStretch
                        >
                            {`Set password`}
                        </Button>
                    </AuthForm>
                )
            ) : null}
            <Bumper />
        </AuthWrapper>
    );
};
